import { FC, Suspense, useEffect, useState } from 'react';
import { ThreeDotLoader } from '../../Fallback/ThreeDotsLoader';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';
import { LearnosityRequest } from '../../../models/LearnosityRequest';
import { useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { useAuth0 } from '@auth0/auth0-react';
import { useLearnosityBffInitialization } from '../../../custom-hooks/useLearnosityBffInitialization';
import { v4 as uuid } from 'uuid';
import DiagnosticAssessmentDemoWrapper from '../../DiagnosticAssessmentDemoWrapper/DiagnosticAssessmentDemoWrapper';
import { diagnosticDemoRegions } from '../../../learnosity-regions/DiagnosticDemoRegions';
import { Tooltip } from 'primereact/tooltip';
import EphemeralMessage from '../../StudentSingleSkill/EphemeralMessage';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../../utils/fetchCancellation';
import { ErrorBoundaryUI } from '../../ErrorBoundary/ErrorBoundaryUI';

const TeacherDiagnosticPreview: FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const { getAccessTokenSilently, logout } = useAuth0();
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );

  const gradeLevels = [
    { name: '4th Grade', activityId: 'TeacherIntro_Diagnostic_4' },
    { name: '5th Grade', activityId: 'TeacherIntro_Diagnostic_5' },
    { name: '6th Grade', activityId: 'TeacherIntro_Diagnostic_6' },
    { name: '7th Grade', activityId: 'TeacherIntro_Diagnostic_7' },
    { name: '8th Grade', activityId: 'TeacherIntro_Diagnostic_8' },
    { name: 'Algebra', activityId: 'TeacherIntro_Diagnostic_Alg' },
  ];
  const [selectedGradeLevel, setSelectedGradeLevel] = useState<{ name: string; activityId: string } | null>(null);
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const [failedStartup, setFailedStartup] = useState<string | null>(null);
  const startupKey = user?.sub && selectedGradeLevel ? `${user.sub}|${selectedGradeLevel.activityId}` : null;
  const [testQuestion, setTestQuestion] = useState<number>(0);
  const [tooltipKey, setTooltipKey] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [arrowMessageShown, setArrowMessageShown] = useState<boolean>(false);
  const [learnosityKey, setLearnosityKey] = useState<number>(0);

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    setFailedStartup(null);
    if (user && user.sub && selectedGradeLevel) {
      // Each selected assessment gets a session; HTTP retries reuse these params.
      const sessionId = uuid();
      fetchInitialization(
        {
          userId: user?.sub,
          sessionId: sessionId,
          state: 'initial',
          activityId: selectedGradeLevel.activityId,
          title: `${selectedGradeLevel.name} diagnostic`,
          regions: diagnosticDemoRegions,
          renderingType: 'assess',
          responsiveRegions: true,
          showIntro: false,
          warningOnChange: false,
          scrollToTest: false,
          scrollToTop: false,
        },
        { signal: controller.signal },
      )
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
          setLearnosityKey(new Date().getTime());
          setLoading(true);
          setTestQuestion(0);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setFailedStartup(startupKey);
          setLoading(false);
          console.log('Error fetching Learnosity bridge');
        });
    }
    return () => controller.abort();
  }, [selectedGradeLevel, user?.sub, startupKey]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }, []);

  return (
    <Suspense fallback={<ThreeDotLoader dark />}>
      <Tooltip key={tooltipKey} target=".nc-tooltip-wrapper" content="Disabled for the preview" position="top" />
      <div className={`${selectedGradeLevel ? 'tw-h-[100%]' : 'tw-h-[100vh]'}  tw-bg-indigo-50`}>
        <div className={`nc_page-inner xl:tw-w-10/12 tw-mx-auto tw-w-11/12 tw-relative`}>
          <Button
            label="Back to Prep page"
            link
            className="tw-gap-x-2"
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate('/prep-diagnostics')}
          />
          <h1 className="tw-text-2xl tw-text-neutral-950 tw-font-sora tw-font-bold tw-my-6 tw-pb-4 tw-border-b-[2px] tw-border-t-[0px] tw-border-x-[0px] tw-border-indigo-100 tw-border-solid ">
            Diagnostic preview
          </h1>
          <div className="tw-flex tw-flex-col tw-items-start tw-justify-center tw-gap-y-4">
            <div className="p-inputgroup tw-w-auto">
              <Button label="Grades" className="!tw-bg-neutral-50 !tw-text-neutral-950" />
              <Dropdown
                value={selectedGradeLevel}
                onChange={e => setSelectedGradeLevel(e.value)}
                options={gradeLevels}
                optionLabel="name"
                placeholder="Select a Grade Level"
                className="w-full md:w-14rem"
              />
            </div>
            {failedStartup !== null && failedStartup === startupKey && <ErrorBoundaryUI embedded />}
            {request && (
              <DiagnosticAssessmentDemoWrapper
                key={learnosityKey}
                setLoading={setLoading}
                loading={loading}
                setTooltipKey={setTooltipKey}
                request={request!}
                refetchInitialization={refetchInitialization}
                selectedGradeLevel={selectedGradeLevel}
                testQuestion={testQuestion}
                setTestQuestion={setTestQuestion}
                showArrowMessage={!arrowMessageShown}
                onArrowMessageShown={() => setArrowMessageShown(true)}
              />
            )}
          </div>
        </div>
        <EphemeralMessage
          delay={1}
          text="As a teacher, you can review the diagnostic for any grade that we offer"
          ease={[0.34, 1, 1, 1]}
          duration={1}
          customStyle={{
            top: '18.5rem',
            left: '15rem',
          }}
          strokePositionType="top"
        />
      </div>
    </Suspense>
  );
};

export default TeacherDiagnosticPreview;
